import { causasMexidas, diferencasDeQueda, MATERIAIS_TALABARTE, type ResumoQueda } from '../../dominio/tabletop'

const virgula = (valor: number, casas = 1) => valor.toFixed(casas).replace('.', ',')

const parada = (r: ResumoQueda) => (r.bateuEm ? `bate em ${r.bateuEm}` : `para a ${virgula(r.pesFinaisY ?? 0)} m de altura`)

/**
 * A queda atual ao lado da queda guardada como referência: o que mudou entre
 * as duas, e pico, tempo, descida e parada de cada uma. O menor pico leva o
 * destaque, e se mais de uma coisa mudou o aviso lembra que a diferença mistura
 * causas.
 */
export function ComparacaoQueda({ referencia, atual }: { referencia: ResumoQueda; atual: ResumoQueda }) {
  const diferencas = diferencasDeQueda(referencia, atual)
  const causas = causasMexidas(diferencas)
  const menorPico = referencia.forcaPicoKN === atual.forcaPicoKN ? null : referencia.forcaPicoKN < atual.forcaPicoKN ? 'referencia' : 'atual'
  const linhas: ReadonlyArray<readonly [string, string, string]> = [
    ['Pico de força', `${virgula(referencia.forcaPicoKN)} kN`, `${virgula(atual.forcaPicoKN)} kN`],
    ['Na ancoragem', `${virgula(referencia.forcaNaAncoragemKN)} kN`, `${virgula(atual.forcaNaAncoragemKN)} kN`],
    ['Até parar', `${Math.round(referencia.duracaoS * 1000)} ms`, `${Math.round(atual.duracaoS * 1000)} ms`],
    ['Descida depois de esticar', `${virgula(referencia.descidaAposEsticarM, 2)} m`, `${virgula(atual.descidaAposEsticarM, 2)} m`],
    ['Redutor abriu', `${virgula(referencia.aberturaUsadaM, 2)} m`, `${virgula(atual.aberturaUsadaM, 2)} m`],
    ['Fica', parada(referencia), parada(atual)],
  ]

  return (
    <div className="comparacao-queda-bloco" aria-live="polite">
      {diferencas.length === 0 ? (
        <p className="dica">Igual à referência. Mude uma coisa (o material, a massa, o absorvedor ou a ancoragem) e simule de novo.</p>
      ) : (
        <>
          <p className="corda-resumo">
            <strong>O que mudou</strong>
          </p>
          <ul className="lista-pontos-ancoragem">
            {diferencas.map((d) => (
              <li key={d.chave}>
                {d.rotulo}: {d.antes} → <strong>{d.depois}</strong>
              </li>
            ))}
          </ul>
          {causas > 1 && (
            <p className="aviso-corda" role="note">
              Mais de uma coisa mudou: a diferença nos números mistura as causas. Para ver o efeito de cada uma, mude uma de cada vez.
            </p>
          )}
        </>
      )}
      <table className="comparacao-queda">
        <thead>
          <tr>
            <th scope="col"> </th>
            <th scope="col">Referência</th>
            <th scope="col">Esta queda</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map(([rotulo, ref, atu], i) => (
            <tr key={rotulo}>
              <th scope="row">{rotulo}</th>
              <td className={i === 0 && menorPico === 'referencia' ? 'comparacao-queda__melhor' : undefined}>{ref}</td>
              <td className={i === 0 && menorPico === 'atual' ? 'comparacao-queda__melhor' : undefined}>{atu}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Uma linha que diz o que está guardado como referência. */
export function descricaoDaReferencia(r: ResumoQueda): string {
  return `${r.nomeTrabalhador} · ${MATERIAIS_TALABARTE[r.material].nome} · ${r.massaKg} kg · pico ${virgula(r.forcaPicoKN)} kN`
}
